import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LoadingOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import UseAPI from 'lib/api/UseAPI';
import api_config from 'lib/api/common';
import validator from 'validator';
import Logo from '../../assets/img/logo.png';
import LPPromoItem1 from './LPPromoItem1';
import { StoreContext } from 'store';
import { GoogleLogin } from '@react-oauth/google';
import AuthContainer from './AuthContainer';

import './index.css';
import { getReferralCodeFromCookie } from './CookieUtil';
import { trackFirstRunEvent } from '../../lib/utils/firstRunEvents';
import { ErrorMessage, FormContainer, FormInputField, LoginButton, TermsText, PasswordInputContainer, PasswordToggleIcon } from './AuthStyles';
import useCustomNotification from '../../hooks/useCustomNotification';
import NotificationProvider from '../../components/CustomNotification/NotificationProvider';

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const Login = () => {
  const { notifications, removeNotification, api } = useCustomNotification();
  const openNotificationWithIcon = (type, message, desc) => {
    api[type](message, desc);
  };
  const navigate = useNavigate();
  const location = useLocation();

  const { state, dispatch } = useContext(StoreContext);

  let oldTitle = document.title;
  useEffect(() => {
    document.title = "Log in | Sayify";
    return () => {
      document.title = oldTitle;
    };
  }, []);

  useEffect(() => {

    const params = new URLSearchParams(location.search);
    const nextUrl = params.get('next');

    // Store `next` parameter in session storage if it exists
    if (nextUrl) {
      sessionStorage.setItem('nextUrl', nextUrl);
    }

    dispatch({ type: 'UPDATE_STATE', payload: { show_chat_widget: false } });
    return () => { };
  }, []);

  const [user, setUser] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isEnableSignUp, setEnableSignUp] = useState(false);
  const [isUserLoading, setUserloading] = useState(false);

  const [errMsg, setErrMsg] = useState('');

  const handleChange = (e) => {
    setUser(e.target.value);
  };

  const handleLogIn = async (event) => {
    event.preventDefault();

    setUserloading(true);
    let payload;
    if (validator.isEmail(user)) {
      payload = {
        username: user.trim(),
        password: pwd.trim(),
      };
    } else {
      setUserloading(false);
      openNotificationWithIcon('error', 'Failed to login', 'Please enter your valid email');
      return;
    }

    let url = api_config.BASE_BACKEND_ENDPOINT + 'api/login/';
    api_config.ABORT_CONTROLLER = new AbortController();
    const signal = api_config.ABORT_CONTROLLER.signal;

    try {
      UseAPI(signal, url, 'POST', payload)
        .then(function (data) {
          // handle data here
          if (data.success) {
            const token = data.token;
            localStorage.clear();
            localStorage.setItem('loggedIn', true);
            localStorage.setItem('user-token', token);
            localStorage.setItem('k_', data.user_info["email"] === "codenginebdup@gmail.com");

            // Set GA4 User ID
            import('react-ga4').then((ReactGA) => {
              if (data.user_info && data.user_info.id) {
                ReactGA.default.set({ userId: data.user_info.id });
              }
            });

            openNotificationWithIcon('success', 'Login Success', 'You are now logged in.');

            const nextUrl = sessionStorage.getItem('nextUrl');
            if (nextUrl) {
              sessionStorage.removeItem('nextUrl');
              navigate(nextUrl);
            }
            else {
              navigate('/');
            }

          } else {
            openNotificationWithIcon(
              'error',
              'Failed to login',
              'Your Username or Password is incorrect, please enter valid Username and Password.      ',
            );
          }
          setUserloading(false);
        })
        .catch(function (err) {
          openNotificationWithIcon(
            'error',
            'Failed to login',
            'Your Username or Password is incorrect, please enter valid Username and Password.      ',
          );

          setUserloading(false);
        });
    } catch (e) {
      setUserloading(false);
      openNotificationWithIcon(
        'error',
        'Failed to login',
        'Your Username or Password is incorrect, please enter valid Username and Password.      ',
      );
    }
  };

  return (
    <>
      <NotificationProvider notifications={notifications} onRemove={removeNotification} />
      <AuthContainer
        leftContent={<LPPromoItem1 />}
        logo={<img style={{ width: '140px' }} src={Logo} alt="Sayify.pro" />}
        title="Welcome back"
        subtitle="Sign in to your workspace"
        footerLinks={
          <>
            <p>
              Forgot your password?
              <Link to="/reset-password">Reset now</Link>
            </p>
            <p>
              Don't have an account yet?
              <Link to="/signup">Create new Account</Link>
            </p>
          </>
        }
      >
        <div className="google-login-container">
          <GoogleLogin
            theme='outline'
            size='large'
            text='continue_with'
            shape='pill'
            logo_alignment='left'
            useOneTap='true'
            width='360px'
            onSuccess={credentialResponse => {
              let url = api_config.BASE_BACKEND_ENDPOINT + 'api/social-auth/';
              let apiConfigController = new AbortController();
              const signal = apiConfigController.signal;
              let accessToken = credentialResponse.credential;
              let payload = {
                access_token: accessToken,
                referral_code: getReferralCodeFromCookie()
              }
              UseAPI(signal, url, 'POST', payload)
                .then(function (data) {
                  // handle data here
                  if (data.success) {
                    const token = data.token;
                    localStorage.clear();
                    localStorage.setItem('loggedIn', true);
                    localStorage.setItem('user-token', token);

                    import('react-ga4').then((ReactGA) => {
                      // Google One Tap / "Continue with Google" creates the
                      // account when it doesn't exist yet — that's a signup
                      // conversion even though it happened on the login page.
                      if (data.is_new_user) {
                        if (data.user_info && data.user_info.email) {
                          // Enhanced conversions: gtag hashes the email before sending.
                          ReactGA.default.gtag('set', 'user_data', { email: data.user_info.email });
                        }
                        ReactGA.default.event({
                          category: 'User',
                          action: 'Signup',
                          label: 'Google'
                        });
                      }
                      if (data.user_info && data.user_info.id) {
                        ReactGA.default.set({ userId: data.user_info.id });
                      }
                    });

                    if (data.is_new_user) {
                      trackFirstRunEvent('signup_completed', { method: 'google' });
                    }

                    openNotificationWithIcon('success', 'Login Success', 'You are now logged in.');
                    const nextUrl = sessionStorage.getItem('nextUrl');
                    if (nextUrl) {
                      sessionStorage.removeItem('nextUrl');
                      navigate(nextUrl);
                    }
                    else {
                      navigate('/');
                    }
                  } else {
                    openNotificationWithIcon(
                      'error',
                      'Login Failed',
                      data.message || 'We couldn\'t sign you in. Please check your credentials or try again later.',
                    );
                  }
                  setUserloading(false);
                })
                .catch(function (err) {
                  openNotificationWithIcon(
                    'error',
                    'Login Failed',
                    'We couldn\'t sign you in. Please check your credentials or try again later.',
                  );

                  setUserloading(false);
                });
            }}
            onError={() => {
              openNotificationWithIcon('error', 'Failed to login', 'Could not login. Please retry again later.');
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', margin: '1rem 0' }}>
          <div style={{ flex: 1, height: '1px', background: '#DDD4CC' }}></div>
          <span style={{ padding: '0 1rem', color: '#6A625B', fontSize: '0.875rem', fontWeight: '500' }}>OR</span>
          <div style={{ flex: 1, height: '1px', background: '#DDD4CC' }}></div>
        </div>

        <FormContainer className="auth-form" onSubmit={handleLogIn}>
          <FormInputField
            type="email"
            id="username"
            placeholder="Enter your email"
            autoComplete="off"
            onChange={handleChange}
            value={user}
            required
          />

          <PasswordInputContainer>
            <FormInputField
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              name="password"
              aria-label="Password"
              required
              disabled={isUserLoading}
              onChange={(e) => setPwd(e.target.value)}
              value={pwd}
            />
            <PasswordToggleIcon
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            </PasswordToggleIcon>
          </PasswordInputContainer>
          <TermsText style={{ marginTop: '7px' }}>By signing in, I agree to the <a style={{ textDecoration: 'underline' }} target='_blank' href="https://sayify.pro/privacy/">privacy policy</a> and <a style={{ textDecoration: 'underline' }} target='_blank' href="https://sayify.pro/terms/">terms and conditions</a>.</TermsText>
          {errMsg && <ErrorMessage className="error-message">{errMsg}</ErrorMessage>}
          <LoginButton>
            {isUserLoading && (
              <Spin
                indicator={antIcon}
                style={{
                  color: 'white',
                  marginRight: '10px',
                  marginTop: '3px',
                }}
              />
            )}
            <span style={{ fontSize: '16px' }}> {isUserLoading ? 'Logging in...' : 'Login'} </span>{' '}
          </LoginButton>
        </FormContainer>
      </AuthContainer>
    </>
  );
};

export default Login;
